import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  return apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SourceBadge({ source }) {
  const s = String(source || "").toLowerCase();
  const isApp = s === "app";
  const label = isApp ? "App" : s === "web" ? "Web" : "-";
  const bg = isApp ? "#ecfdf5" : "#eff6ff";
  const color = isApp ? "#047857" : "#1d4ed8";
  const border = isApp ? "#a7f3d0" : "#bfdbfe";
  if (label === "-") return <span style={{ color: "#94a3b8" }}>-</span>;
  return (
    <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
      {label}
    </span>
  );
}

export default function FormSubmissionsPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchData = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ page: String(nextPage), limit: String(limit) });
      if (search) params.set("search", search);
      const res = await fetch(`${getBaseUrl()}/api/form-submissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRows(Array.isArray(data.data) ? data.data : []);
        setTotalPages(data.meta?.totalPages || 1);
        setTotal(data.meta?.total || 0);
        setPage(data.meta?.page || nextPage);
      } else {
        setRows([]);
        toast.error(data.message || "Failed to fetch form submissions");
      }
    } catch (e) {
      setRows([]);
      toast.error("Failed to fetch form submissions");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(query.trim());
  };

  const resetFilters = () => {
    setQuery("");
    setSearch("");
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, color: THEME, fontWeight: 700 }}>
              <i className="fa-solid fa-clipboard-check" style={{ marginRight: 8 }} />
              Form Submissions
            </h3>
          </div>
          <form onSubmit={onSearchSubmit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search form, user..."
              style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 13, minWidth: 220 }}
            />
            <button type="submit" style={{ background: THEME, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Search
            </button>
            <button type="button" onClick={resetFilters} style={{ background: "#fff", color: THEME, border: `1px solid ${THEME}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Reset
            </button>
          </form>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: "left", color: THEME, borderBottom: "2px solid #f1e3e8" }}>
                <th style={{ padding: "12px 10px", fontSize: 13 }}>#</th>
                <th style={{ padding: "12px 10px", fontSize: 13 }}>Form</th>
                <th style={{ padding: "12px 10px", fontSize: 13 }}>Filled By</th>
                <th style={{ padding: "12px 10px", fontSize: 13 }}>Source</th>
                <th style={{ padding: "12px 10px", fontSize: 13 }}>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No form submissions found</td></tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 10px", fontSize: 13, color: "#64748b" }}>{(page - 1) * limit + idx + 1}</td>
                    <td style={{ padding: "12px 10px", fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{row.formName || "-"}</td>
                    <td style={{ padding: "12px 10px", fontSize: 13, color: "#1e293b" }}>{row.userName || "-"}</td>
                    <td style={{ padding: "12px 10px", fontSize: 13 }}><SourceBadge source={row.clientSource} /></td>
                    <td style={{ padding: "12px 10px", fontSize: 13, color: "#475569" }}>{formatDateTime(row.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => fetchData(page - 1)}
              style={{ background: "#fff", color: page <= 1 ? "#cbd5e1" : THEME, border: `1px solid ${page <= 1 ? "#e2e8f0" : THEME}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: page <= 1 ? "not-allowed" : "pointer" }}
            >
              Prev
            </button>
            <span style={{ fontSize: 13, color: "#475569" }}>{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => fetchData(page + 1)}
              style={{ background: "#fff", color: page >= totalPages ? "#cbd5e1" : THEME, border: `1px solid ${page >= totalPages ? "#e2e8f0" : THEME}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: page >= totalPages ? "not-allowed" : "pointer" }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
